import { useEffect, useMemo } from 'react';
import {
  AlignmentStatus,
  AssetOverviewData,
  DirectionalAgreementData,
  OrderBookData,
  OrderPositionEntry,
  OrderPositionsData,
  TradeDirection,
  TradeRowData,
  TradeSentiment,
  TradeStatus,
} from '@monorepo/community-stats-client';
import {
  BiasLabel,
  CoreMetricsData,
  DirectionalAgreementData as ApiAgreementData,
  DirectionalAnalyticsRow,
  MarketStatsFilterOptions,
  MarketStatsFilterParams,
  OpenOrdersData,
  PayoutTickerItem,
  PinnedAssetDetail,
  PositionsMapData,
  RRRealizationData,
  WinLossDurationData,
} from '@monorepo/community-stats-common';
import { useStore } from '@hub/helpers/use-store';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import {
  fetchAgreementGauge,
  fetchAssetDetail,
  fetchCoreMetrics,
  fetchDirectionalAnalytics,
  fetchDurationAnalysis,
  fetchMarketStatsFilters,
  fetchOpenOrders,
  fetchPayoutTicker,
  fetchPinnedAssets,
  fetchPositionsMap,
  fetchRRRealization,
  pinAsset,
  unpinAsset,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { ExtractFnReturnType } from '@hub/utils/react-query/react-query';
import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.COMMUNITY_STATS;
const PUSHER_CHANNEL = 'market-stats-payout-ticker';
const PUSHER_EVENT = 'new-payout';
const MAX_PAYOUT_TICKER_ITEMS = 100;

function useCommunityStatsQuery<TData>(
  options: Omit<UseQueryOptions<TData, Error, TData, QueryKey>, 'staleTime'>,
) {
  return useQuery<TData>({
    ...options,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
}

export const usePayoutTicker = () => {
  const queryClient = useQueryClient();
  const {
    dataStore: { realTimeStore },
  } = useStore();

  const query = useCommunityStatsQuery<
    ExtractFnReturnType<typeof fetchPayoutTicker>
  >({
    queryKey: QUERY_KEYS[CONFIG_KEY].PAYOUT_TICKER,
    queryFn: fetchPayoutTicker,
  });

  useEffect(() => {
    realTimeStore.registerToEvent<PayoutTickerItem>(
      PUSHER_CHANNEL,
      PUSHER_EVENT,
      (newItem) => {
        queryClient.setQueryData<PayoutTickerItem[]>(
          QUERY_KEYS[CONFIG_KEY].PAYOUT_TICKER,
          (prev) => {
            const updated = prev
              ? [newItem, ...prev].slice(0, MAX_PAYOUT_TICKER_ITEMS)
              : [newItem];

            return updated;
          },
        );
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return query;
};

export const useMarketStatsFilters = () => {
  return useCommunityStatsQuery<MarketStatsFilterOptions | null>({
    queryKey: QUERY_KEYS[CONFIG_KEY].FILTERS,
    queryFn: fetchMarketStatsFilters,
  });
};

export const useDurationAnalysis = (
  symbol?: string | null,
  filters?: MarketStatsFilterParams,
) => {
  return useCommunityStatsQuery<WinLossDurationData[]>({
    queryKey: QUERY_KEYS[CONFIG_KEY].DURATION_ANALYSIS(
      symbol ?? undefined,
      filters,
    ),
    queryFn: () => fetchDurationAnalysis(symbol ?? undefined, filters),
    enabled: !!symbol,
  });
};

export const useCoreMetrics = (filters?: MarketStatsFilterParams) => {
  return useCommunityStatsQuery<CoreMetricsData | null>({
    queryKey: QUERY_KEYS[CONFIG_KEY].CORE_METRICS(filters),
    queryFn: () => fetchCoreMetrics(filters),
  });
};

const toAlignmentStatus = (
  state: 'Aligned' | 'Mixed' | 'No Data',
): AlignmentStatus => {
  if (state === 'Aligned') return AlignmentStatus.ALIGNED;
  if (state === 'No Data') return AlignmentStatus.FRAGMENTED;
  return AlignmentStatus.MIXED;
};

const mapApiToComponentData = (
  row: ApiAgreementData,
): DirectionalAgreementData => ({
  agreementScore: row.agreementScore ?? 0,
  scoreDescription: row.agreementLabel ?? 'No Data',
  alignmentLabel: row.agreementLabel ?? 'No Data',
  sampleCount: 0,
  breakdown: {
    positions: toAlignmentStatus(row.positionState ?? 'No Data'),
    volume: toAlignmentStatus(row.volumeState ?? 'No Data'),
    recentFlow: toAlignmentStatus(row.flowState ?? 'No Data'),
  },
});

const BIAS_TO_DIRECTION: Record<BiasLabel, TradeDirection> = {
  'Strong Long': TradeDirection.StrongLong,
  Long: TradeDirection.Long,
  Neutral: TradeDirection.Neutral,
  Short: TradeDirection.Short,
  'Strong Short': TradeDirection.StrongShort,
};

const BIAS_TO_SENTIMENT: Record<BiasLabel, TradeSentiment> = {
  'Strong Long': TradeSentiment.StrongLong,
  Long: TradeSentiment.Long,
  Neutral: TradeSentiment.Neutral,
  Short: TradeSentiment.Short,
  'Strong Short': TradeSentiment.StrongShort,
};

const toNullable = (value: number): number | null =>
  value === 0 ? null : value;

const mapAnalyticsToTradeRow = (
  row: DirectionalAnalyticsRow,
  index: number,
): TradeRowData => {
  const symbol = row.symbol ?? '';
  return {
    id: `da-${symbol}-${index}`,
    symbol,
    abbreviation: symbol.replace(/[^A-Z]/g, '').substring(0, 2),
    direction:
      BIAS_TO_DIRECTION[row.positionBiasLabel] ?? TradeDirection.Neutral,
    sentiment: BIAS_TO_SENTIMENT[row.volumeBiasLabel] ?? TradeSentiment.Neutral,
    status: TradeStatus.Pending,
    isActive: false,
    isPinned: false,
    metrics: {
      positionsLong: toNullable(row.avgEntryLong ?? 0),
      positionsShort: toNullable(row.avgEntryShort ?? 0),
      volumeLong: toNullable(row.avgSLLong ?? 0),
      volumeShort: toNullable(row.avgSLShort ?? 0),
      metric3Long: toNullable(row.avgTPLong ?? 0),
      metric3Short: toNullable(row.avgTPShort ?? 0),
    },
  };
};

const DEFAULT_TABLE_LIMIT = 8;

export const useDirectionalAnalytics = (
  filters?: MarketStatsFilterParams,
  options?: { search?: string; limit?: number },
) => {
  const search = options?.search || undefined;
  const limit = search ? undefined : (options?.limit ?? DEFAULT_TABLE_LIMIT);

  const query = useCommunityStatsQuery<DirectionalAnalyticsRow[]>({
    queryKey: QUERY_KEYS[CONFIG_KEY].DIRECTIONAL_ANALYTICS(filters, {
      search,
      limit,
    }),
    queryFn: () => fetchDirectionalAnalytics(filters, { search, limit }),
  });

  const data = useMemo(
    () => (query.data ? query.data.map(mapAnalyticsToTradeRow) : []),
    [query.data],
  );

  return { ...query, data };
};

export const useAgreementGauge = (
  symbol?: string,
  filters?: MarketStatsFilterParams,
) => {
  const query = useCommunityStatsQuery<ApiAgreementData[]>({
    queryKey: QUERY_KEYS[CONFIG_KEY].AGREEMENT_GAUGE(symbol, filters),
    queryFn: () => fetchAgreementGauge(symbol, filters),
  });

  const data = useMemo(
    () => (query.data ? query.data.map(mapApiToComponentData) : []),
    [query.data],
  );

  return { ...query, data };
};

const mapPinnedAssetToOverviewData = (
  detail: PinnedAssetDetail,
): AssetOverviewData => {
  const tags = detail.tags ?? [];
  const longVolume = tags.includes('Long Volume');
  const shortVolume = tags.includes('Short Volume');
  const volumeTag = longVolume
    ? ('pinnedAssetOverview.tags.longVolume' as const)
    : shortVolume
      ? ('pinnedAssetOverview.tags.shortVolume' as const)
      : ('pinnedAssetOverview.tags.neutralVolume' as const);

  const sentiment = detail.communitySentiment ?? 50;
  const positionTag =
    sentiment >= 65
      ? ('pinnedAssetOverview.tags.strongLong' as const)
      : sentiment >= 55
        ? ('pinnedAssetOverview.tags.long' as const)
        : sentiment <= 35
          ? ('pinnedAssetOverview.tags.strongShort' as const)
          : sentiment <= 45
            ? ('pinnedAssetOverview.tags.short' as const)
            : ('pinnedAssetOverview.tags.neutralPositions' as const);

  const symbolStr = detail.symbol ?? '';

  return {
    symbol: {
      symbol: symbolStr,
      name: symbolStr,
      abbreviation: symbolStr.replace(/[^A-Z]/g, '').substring(0, 2),
      category: (detail.assetClass ??
        'Forex') as AssetOverviewData['symbol']['category'],
    },
    priceData: {
      currentPrice: detail.currentPrice ?? 0,
      priceChange: detail.change24h ?? 0,
      priceChangePercent: detail.changePercent24h ?? 0,
      currency: 'USD',
    },
    marketData: {
      high24h: detail.high24h ?? 0,
      low24h: detail.low24h ?? 0,
      marketCap: detail.marketCap ?? 0,
      marketCapFormatted: detail.marketCap
        ? `$${(detail.marketCap / 1e9).toFixed(1)}B`
        : null,
      supply: detail.circulatingSupply ?? 0,
      supplyFormatted: detail.circulatingSupply
        ? `${(detail.circulatingSupply / 1e6).toFixed(1)}M`
        : null,
      supplyUnit: symbolStr.replace(/[^A-Z]/g, '').substring(0, 3),
    },
    sentimentData: {
      assetSentimentPercent: sentiment,
      marketSentimentPercent: 100 - sentiment,
    },
    tags: { volumeTag, positionTag },
  };
};

export const useAssetDetail = (
  symbol: string | null,
  filters?: MarketStatsFilterParams,
) => {
  const query = useCommunityStatsQuery<PinnedAssetDetail | null>({
    queryKey: QUERY_KEYS[CONFIG_KEY].ASSET_DETAIL(symbol ?? '', filters),
    queryFn: () => fetchAssetDetail(symbol!, filters),
    enabled: !!symbol,
  });

  const data = useMemo(
    () => (query.data ? mapPinnedAssetToOverviewData(query.data) : undefined),
    [query.data],
  );

  return { ...query, data };
};

export const usePinnedAssets = () => {
  return useCommunityStatsQuery<string[]>({
    queryKey: QUERY_KEYS[CONFIG_KEY].PINNED_ASSETS,
    queryFn: fetchPinnedAssets,
  });
};

export const useRRRealization = (
  symbol?: string | null,
  filters?: MarketStatsFilterParams,
) => {
  return useCommunityStatsQuery<RRRealizationData[]>({
    queryKey: QUERY_KEYS[CONFIG_KEY].RR_REALIZATION(
      symbol ?? undefined,
      filters,
    ),
    queryFn: () => fetchRRRealization(symbol ?? undefined, filters),
    enabled: !!symbol,
  });
};

export const usePinAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (symbol: string) => pinAsset(symbol),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[CONFIG_KEY].PINNED_ASSETS,
      });
    },
  });
};

export const useUnpinAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (symbol: string) => unpinAsset(symbol),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS[CONFIG_KEY].PINNED_ASSETS,
      });
    },
  });
};

/**
 * Maps the API OpenOrdersData shape to the component OrderBookData shape.
 * API uses totalVolume/cumulativeVolume; component uses quantity/cumulativeQuantity.
 * API bestBid/bestAsk are plain numbers; component expects {price, quantity}.
 */
const mapOpenOrdersToComponentData = (api: OpenOrdersData): OrderBookData => {
  const bids = api.bids ?? [];
  const asks = api.asks ?? [];
  return {
    bids: bids.map((b) => ({
      price: b.price,
      quantity: b.totalVolume,
      cumulativeQuantity: b.cumulativeVolume,
    })),
    asks: asks.map((a) => ({
      price: a.price,
      quantity: a.totalVolume,
      cumulativeQuantity: a.cumulativeVolume,
    })),
    bestBid: {
      price: Number.isFinite(api.bestBid) ? api.bestBid : 0,
      quantity: bids.find((b) => b.price === api.bestBid)?.totalVolume ?? 0,
    },
    bestAsk: {
      price: Number.isFinite(api.bestAsk) ? api.bestAsk : 0,
      quantity: asks.find((a) => a.price === api.bestAsk)?.totalVolume ?? 0,
    },
  };
};

export const useOpenOrders = (
  symbol: string | null,
  filters?: MarketStatsFilterParams,
) => {
  const query = useCommunityStatsQuery<OpenOrdersData | null>({
    queryKey: QUERY_KEYS[CONFIG_KEY].OPEN_ORDERS(symbol ?? '', filters),
    queryFn: () => fetchOpenOrders(symbol!, filters),
    enabled: !!symbol,
  });

  const data = useMemo(
    () => (query.data ? mapOpenOrdersToComponentData(query.data) : undefined),
    [query.data],
  );

  return { ...query, data };
};

/**
 * Maps the API PositionsMapData shape to the component OrderPositionsData shape.
 *
 * Bar widths use sqrt-compressed scaling: bars are proportional to each other
 * (8000 lots renders much wider than 1 lot) while small values (0.01 lots) still
 * render as a visible sliver rather than disappearing entirely.
 *
 * currentPrice is derived as a simple average of all price levels (median-like)
 * so that a single outlier volume level does not skew the price indicator position.
 *
 * Raw longVolume/shortVolume are passed through for tooltip display.
 */
const mapPositionsMapToComponentData = (
  api: PositionsMapData,
): OrderPositionsData => {
  const levels = api.levels ?? [];

  const allVolumes = levels
    .flatMap((l) => [l.longVolume ?? 0, l.shortVolume ?? 0])
    .filter((v) => v > 0);

  const maxVolume = allVolumes.length > 0 ? Math.max(...allVolumes) : 1;
  const sqrtMax = Math.sqrt(maxVolume);

  const scale = (v: number): number =>
    sqrtMax > 0 ? (Math.sqrt(v) / sqrtMax) * 100 : 0;

  const currentPrice =
    levels.length > 0
      ? levels.reduce((sum, l) => sum + l.priceLevel, 0) / levels.length
      : 0;

  const positions: OrderPositionEntry[] = levels.map((l) => ({
    price: l.priceLevel,
    longPercentage: scale(l.longVolume),
    shortPercentage: scale(l.shortVolume),
    netLongPercentage: l.netSide === 'long' ? scale(l.netVolume) : 0,
    netShortPercentage: l.netSide === 'short' ? scale(l.netVolume) : 0,
    longVolume: l.longVolume,
    shortVolume: l.shortVolume,
  }));

  return { positions, currentPrice };
};

export const usePositionsMap = (
  symbol: string | null,
  filters?: MarketStatsFilterParams,
) => {
  const query = useCommunityStatsQuery<PositionsMapData | null>({
    queryKey: QUERY_KEYS[CONFIG_KEY].POSITIONS_MAP(symbol ?? '', filters),
    queryFn: () => fetchPositionsMap(symbol!, filters),
    enabled: !!symbol,
  });

  const data = useMemo(
    () => (query.data ? mapPositionsMapToComponentData(query.data) : undefined),
    [query.data],
  );

  return { ...query, data };
};
